import { AxiosError, AxiosResponse } from 'axios'
import {
  errorLogger,
  getErrorMessages,
  isAdminRole,
  LoadingTypes,
  startLoading,
  stopLoading,
  updateAppAlert,
} from '@labrav/common'

import {
  getListingDataById,
  getProductDataFromLink,
  putListingData,
} from '../../../api/marketplace/index'
import {
  DefaultSuccesrOnSubmitData,
  RequestFailureMessage,
} from '../../../common/utils/messagesContants'
import type { AppDispatch, RootState } from '../../../store/types'
import { PartnerType } from '../../../types/external'
import type { TemplateInCreation } from '../templateCreation/types'
import {
  ListingCardCreationActionTypes,
  type ListingCardData,
  type ListingCardInCreation,
} from './types'
import { transformDataFromGet } from './utils/transformDataFromGet'

export { ListingCardCreationActionTypes }
export { transformDataFromGet } from './utils/transformDataFromGet'

export const setListingCardFormData = (integrationData: ListingCardData) => ({
  type: ListingCardCreationActionTypes.SET_CREATE_LISTING_DATA,
  payload: integrationData,
})

export const setTemplateOnListing = (integrationData: TemplateInCreation) => ({
  type: ListingCardCreationActionTypes.SET_TEMPLATE_ON_LISTING,
  payload: integrationData,
})
export const getListingById =
  (listingId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.CREATE_LISTING_CARD))
    await dispatch(clearListingCardData())
    try {
      const response = await getListingDataById(listingId)
      const { data } = response
      const updatedData = transformDataFromGet(data as Record<string, unknown>)
      dispatch(setListingCardFormData(updatedData))
    } catch (e) {
      const errorMessage = getErrorMessages([
        'Unable to fetch details for the listing.',
      ])(e as AxiosResponse<ErrorResponse>)

      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.CREATE_LISTING_CARD))
    }
  }

export const fetchProductDetailsFromUrl =
  (productId: string, index: number, listingData: ListingCardInCreation) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.PRODUCT_INFORMATION))
    try {
      const state = getState()
      const isAdmin = isAdminRole(state.userProfile.userProfile)
      const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
      const partnerId =
        state.AccountData?.[partnerType]?.partnerData?.partnerId || ''
      const response = await getProductDataFromLink(partnerId, productId)
      const { data } = response as {
        data: { product_data?: Record<string, unknown> }
      }
      listingData.listingCardData.product[index] = {
        ...listingData.listingCardData.product[index],
        product_id: productId,
        isLoading: false,
        data: { ...data.product_data },
      }
      // dispatch(saveListingData(listingData))
      dispatch(setListingCardFormData(listingData.listingCardData))
    } catch (e) {
      const errorMessage = getErrorMessages([
        'Unable to fetch details for the provided url.',
      ])(e as AxiosResponse<ErrorResponse>)

      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.PRODUCT_INFORMATION))
    }
  }

export const saveListingData =
  (listingData: ListingCardInCreation, showAlert = false) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const {
        integrationId,
        name,
        template,
        cardAlignment,
        logoPosition,
        product,
        type,
        templateId,
      } = listingData.listingCardData
      const cards = product.flatMap(prod => {
        const match = prod.url.match(/prodview-[a-zA-Z0-9]+/)
        return match ? [match[0]] : []
      })
      const updatedData = {
        integration_id: integrationId || null,
        name: name || '',
        template_id: templateId,
        card_alignment: cardAlignment,
        logo_position: logoPosition,
        type: type,
        template: template,
        cards,
      }

      const partnerId = getState().AccountData.user.partnerData?.partnerId || ''
      const { data } = (await putListingData(partnerId, updatedData)) as {
        data: { integration_id?: string; template_id?: string }
      }
      const getIntegrationId = data?.integration_id || null
      const getTemplateId = data?.template_id || null
      const updatedListingCardData: ListingCardData = {
        ...listingData.listingCardData,
        integrationId: getIntegrationId,
        templateId: getTemplateId,
      }
      dispatch(setListingCardFormData(updatedListingCardData))
      if (data.template_id && data?.integration_id) {
        return data.integration_id
      }
      dispatch(
        updateAppAlert({
          message: showAlert ? DefaultSuccesrOnSubmitData : '',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const clearListingCardData = () => ({
  type: ListingCardCreationActionTypes.CLEAR_LISTING_CARD_DATA,
})
